/** @jsx jsx */

import { useState } from 'react';

import { Icon } from '@zapier/zinnia-icons';
import {
  BetaMenuTriggerRoot,
  BetaMenuTriggerPopover,
  BetaMenuTriggerList,
  BetaMenuTriggerItem,
  IconButton,
  ModalDialogRoot,
  ModalDialogHeader,
  ModalDialogBody,
  ModalDialogFooter,
} from '@zapier/design-system';
import { jsx } from '@emotion/core';

import { copyTextToClipboard } from 'app/common/utils/ClipboardUtils';
import AsyncButton from 'app/developer-v3/components/AsyncButton';
import { Status } from 'app/zap-templates/types';
import { useNotify } from 'app/developer-v3/hooks';
import { useDeleteZapTemplate } from 'app/zap-templates/hooks';
import {
  getZapTemplateEditUrl,
  getCreateTestZapUrl,
} from 'app/zap-templates/utils';

type Props = {
  id: string;
  landingPageUrl: string;
  status: Status;
  title: string;
};

function ActionsMenu({ id, landingPageUrl, status, title }: Props) {
  const handleCopy = async (text: string) => {
    try {
      const didCopy = await copyTextToClipboard(text);
      if (didCopy) {
        notify.success('Copied!');
      } else {
        notify.failure('Failed to copy');
      }
    } catch {
      notify.failure('Failed to copy');
    }
  };

  const [isAboutDeleting, setIsAboutDeleting] = useState(false);

  const notify = useNotify();
  const {
    mutate: deleteZapTemplate,
    isMutating: isDeleting,
  } = useDeleteZapTemplate();

  const editUrl = getZapTemplateEditUrl(id);
  const createTestZapUrl = getCreateTestZapUrl(id);

  return (
    <span>
      <BetaMenuTriggerRoot>
        <IconButton
          aria-label="More actions"
          name="navMoreHoriz"
          size="small"
          variant="ghost"
        />
        <BetaMenuTriggerPopover>
          <BetaMenuTriggerList
            aria-label="Manage Zap templates"
            disabledKeys={isDeleting ? ['delete'] : []}
            onAction={key => {
              switch (key) {
                case 'edit':
                  window.open(editUrl.href, '_blank');
                  break;
                case 'create-zap':
                  window.open(createTestZapUrl.href, '_blank');
                  break;
                case 'copy-link':
                  handleCopy(createTestZapUrl.href);
                  break;
                case 'view-landing':
                  window.open(landingPageUrl, '_blank');
                  break;
                case 'copy-id':
                  handleCopy(id);
                  break;
                case 'delete':
                  setIsAboutDeleting(true);
                  break;
              }
            }}
          >
            <BetaMenuTriggerItem id="edit">
              <Icon name="actionEdit" size={20} />
              Edit
            </BetaMenuTriggerItem>
            <BetaMenuTriggerItem id="create-zap">
              <Icon name="miscBoltAlt" size={20} />
              Create Zap
            </BetaMenuTriggerItem>
            <BetaMenuTriggerItem id="copy-link">
              <Icon name="actionLink" size={20} />
              Copy Link
            </BetaMenuTriggerItem>
            <BetaMenuTriggerItem id="view-landing">
              <Icon name="fileGuide" size={20} />
              View Landing Page
            </BetaMenuTriggerItem>
            <BetaMenuTriggerItem id="copy-id">
              <Icon name="actionCopy" size={20} />
              Copy ID ({id})
            </BetaMenuTriggerItem>
            <BetaMenuTriggerItem id="delete">
              <Icon name="actionTrash" size={20} />
              Delete
            </BetaMenuTriggerItem>
          </BetaMenuTriggerList>
        </BetaMenuTriggerPopover>
      </BetaMenuTriggerRoot>
      <ModalDialogRoot
        isOpen={isAboutDeleting}
        onOpenChange={setIsAboutDeleting}
      >
        <ModalDialogHeader>Delete Zap template</ModalDialogHeader>
        <ModalDialogBody>
          Are you sure you want to delete the Zap template? ({title || id})
        </ModalDialogBody>
        <ModalDialogFooter>
          <AsyncButton
            isWorking={isDeleting}
            onPress={async () => {
              await deleteZapTemplate({ id, status });
              setIsAboutDeleting(false);
            }}
            size="small"
            variant="danger"
          >
            Yes, delete
          </AsyncButton>
        </ModalDialogFooter>
      </ModalDialogRoot>
    </span>
  );
}

export default ActionsMenu;
